/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  useRef,
  useState,
  useEffect,
  type ComponentProps,
  type MouseEvent,
} from 'react';
import classnames from 'classnames';
import CloseIcon from 'components/Icons/Exit/Exit';
import TicketIcon from 'components/Icons/Ticket/Ticket';
import ProfileIcon from 'components/Icons/Profile/Profile';
import ProgramIcon from 'components/Icons/Program/Program';
import EpicenterIcon from 'components/Icons/Epicenter/Epicenter';
import GiftIcon from 'components/Icons/Gift/Gift';
import useMount from 'effects/mount';
import useKeys, { type KeyHandlers } from 'effects/keys';
import Submenu from './Submenu/Submenu';
import './Featured.scss';

const DEFAULT_ITEMS: FeaturedItem[] = [];

const DEFAULT_LABELS = {
  close: 'Close',
};

const DEFAULT_ON_CLOSE = () => {};

const letters = 'abcdefghijklmnopqrstuvwxyzæøå'.split('');

interface FeaturedItem extends Omit<
  ComponentProps<'a'>,
  'title' | 'key' | 'children'
> {
  key: string | number;
  title: string;
  children?: ComponentProps<typeof Submenu>['items'];
}

interface FeaturedProps {
  open?: boolean;
  items?: FeaturedItem[];
  labels?: { close: string };
  onClose?: () => void;
}

/**
 * This is the Featured component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */

const Featured = ({
  open = false,
  items = DEFAULT_ITEMS,
  labels = DEFAULT_LABELS,
  onClose = DEFAULT_ON_CLOSE,
}: FeaturedProps) => {
  const container = useRef<HTMLElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const mount = useMount();
  const [current, setCurrent] = useState(0);
  const [active, setActive] = useState<string | number | null>(null);
  const letterKeys: KeyHandlers = {};

  function onLetter({ key }: KeyboardEvent) {
    const startsWithKey = ({ title }: { title: string }) =>
      title[0].toLowerCase() === key;

    // useKeys keeps the handlers from the first render, so `current` is read
    // from the state update, as for up and down.
    setCurrent(prev => {
      // The next one after the current, then wrap around to the first.
      let index = items.findIndex((item, i) => i > prev && startsWithKey(item));

      if (index === -1) {
        index = items.findIndex(startsWithKey);
      }

      return index !== -1 ? index : prev;
    });
  }

  letters.forEach(letter => {
    letterKeys[letter] = onLetter;
  });

  useKeys('featured', open && !active, {
    esc: onClose,
    up: e => {
      e.preventDefault();
      setCurrent(prev => {
        const next = prev - 1;
        if (next < 0) {
          return items.length - 1;
        }

        return next;
      });
    },
    down: e => {
      e.preventDefault();
      setCurrent(prev => {
        const next = prev + 1;
        if (next >= items.length) {
          return 0;
        }

        return next;
      });
    },
    home: e => {
      e.preventDefault();
      setCurrent(0);
    },
    end: e => {
      e.preventDefault();
      setCurrent(items.length - 1);
    },
    ...letterKeys,
  });

  useEffect(() => {
    if (open && !active) {
      const item = container.current!.querySelector<HTMLElement>(
        `.top-level > li:nth-child(${
          current + 1
        }) > button, .top-level > li:nth-child(${current + 1}) > a`
      );
      if (item) {
        item.focus();
      }
    }
  }, [open, active, current]);

  useEffect(() => {
    if (open) {
      document.body.classList.add('menu-open');
    }

    return function cleanup() {
      document.body.classList.remove('menu-open');
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setActive(null);
      setCurrent(0);
    }
  }, [open]);

  useEffect(() => {
    if (!mount && !open) {
      const menuButtonId = container.current!.getAttribute('aria-labelledby');
      const menuButton = document.getElementById(menuButtonId!);
      if (menuButton) {
        menuButton.focus();
      }
    }
  }, [mount, open]);

  const onContainerClick = ({ target }: MouseEvent<HTMLElement>) => {
    if (target === container.current) {
      onClose();
    }
  };

  function renderIcon(className?: string) {
    if (className) {
      if (className.indexOf('ticket') !== -1) {
        return <TicketIcon />;
      }

      if (className.indexOf('user') !== -1) {
        return <ProfileIcon />;
      }

      if (className.indexOf('profile') !== -1) {
        return <ProfileIcon />;
      }

      if (className.indexOf('program') !== -1) {
        return <ProgramIcon />;
      }

      if (className.indexOf('stream') !== -1) {
        return <EpicenterIcon />;
      }

      if (className.indexOf('gift') !== -1) {
        return <GiftIcon />;
      }
    }

    return <span className="default" />;
  }

  return (
    <nav
      ref={container}
      id="submenu"
      aria-labelledby="menubutton"
      className="featured-navigation"
      onClick={onContainerClick}
      role="dialog"
      aria-modal="true"
    >
      <div>
        <button
          className="exit"
          tabIndex={open ? undefined : -1}
          onClick={onClose}
          aria-label={labels.close}
        >
          <span>
            <CloseIcon title={labels.close} />
            <span className="text">{labels.close}</span>
          </span>
        </button>
        <button
          className="exit exit-mobile-sr"
          tabIndex={open ? undefined : -1}
          onClick={onClose}
          aria-label={labels.close}
        >
          <span>
            <CloseIcon title={labels.close} />
            <span className="text">{labels.close}</span>
          </span>
        </button>
        <ul ref={list} className="top-level" role="none">
          {items.map(({ key, className, title, children, ...props }, index) => {
            if (children) {
              return (
                <li key={key} role="none">
                  <button
                    className={classnames({ expanded: key === active })}
                    aria-controls={`sub${key}`}
                    aria-expanded={active === key}
                    aria-label={title}
                    tabIndex={open ? undefined : -1}
                    onClick={() => {
                      setActive(key === active ? null : key);
                      setCurrent(index);
                    }}
                  >
                    <span>
                      <span className="toggle" />
                      <span
                        className="text"
                        dangerouslySetInnerHTML={{ __html: title }}
                      />
                    </span>
                  </button>
                  <Submenu
                    id={`sub${key}`}
                    items={children}
                    open={active === key}
                    onClose={() => {
                      setActive(null);
                    }}
                  />
                </li>
              );
            }

            return (
              <li key={key} role="none">
                <a {...props} tabIndex={open ? undefined : -1}>
                  {renderIcon(className)}
                  <span dangerouslySetInnerHTML={{ __html: title }} />
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};

export default Featured;
